import { createSlice } from "@reduxjs/toolkit";
import { apiClient, apiClientHooks, HTTP_UNAUTHORIZED } from "@/api/apiClient";
import { clearState } from "@/localStorage";
import { ROLES } from "@/modules/accounts/accountConstants";
import { createAppAsyncThunk } from "@/store/createAppAsyncThunk";
import store from "@/store/store";

/*
 @TODO refactor session token handling
 The token is already handled by session reducer in store state. So the
 explicit local storage handling seems to be redundant.
 */

apiClientHooks.onUnauthorized.push(() => {
  store.dispatch(deleteSessionToken());
  clearState();
});

type Role = (typeof ROLES)[keyof typeof ROLES];

const hasAuthorizedRole = (roles: Role[]): boolean =>
  roles.some((role) => role === ROLES.ADMIN || role === ROLES.PROPERTY_MANAGER);

type RequestSessionTokenParams = {
  emailAddress: string;
  password: string;
};

export const requestSessionTokenThunk = createAppAsyncThunk(
  "session/requestSessionToken",
  async (
    { emailAddress, password }: RequestSessionTokenParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient().post("sessions", {
        emailAddress,
        password,
      });
      const { data } = response;

      // redirect if user role is not occupant
      // NOTE: Redirect only necessary when a the requirement says you
      //       can not login as a "Verwalter"
      //

      if (hasAuthorizedRole(data.user.roles)) {
        // window.location = process.env.REACT_APP_MANAGEMENT_URL;
        return rejectWithValue({ code: "unauthorizedRole" });
      }

      return data.token;
    } catch (err: any) {
      if (err?.response?.status === HTTP_UNAUTHORIZED) {
        return rejectWithValue({ messageId: "ErrorMessage.unauthorized" });
      }
      throw err;
    }
  },
);

type CompleteInvitationParams = {
  token: string;
  password: string;
};

export const completeInvitationThunk = createAppAsyncThunk(
  "session/completeInvitation",
  async (
    { token, password }: CompleteInvitationParams,
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await apiClient().post("/accounts/_acceptInvitation", {
        token,
        password,
      });
      const { data } = response;
      if (data.sessionToken && data.finished) {
        return data.sessionToken;
      } else {
        return rejectWithValue({
          messageId: "ErrorMessage.invitationIncomplete",
        });
      }
    } catch (err: any) {
      console.error("Error while completing invitation.", err);
      return rejectWithValue(err.response.data);
    }
  },
);

const sessionSlice = createSlice({
  name: "session",
  initialState: "" as string,
  reducers: {
    deleteSessionToken: () => "",
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestSessionTokenThunk.fulfilled, (_state, action) => {
        return action.payload;
      })
      .addCase(completeInvitationThunk.fulfilled, (_state, action) => {
        return action.payload;
      });
  },
});

export const { deleteSessionToken } = sessionSlice.actions;

export const reducer = sessionSlice.reducer;
